import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { MapBounds } from 'shared/map/interfaces';
import { SimpleHead } from 'shared/page/Head';
import { ResultPageMapLayout } from 'shared/page/map/ResultPageMapLayout';
import { getApiUrl } from '@/api/apiUrl';
import { fetchOffer } from '@/api/fetcher';
import { Filters } from '@/components/filters/Filters';
import { ResultList } from '@/components/resultList/ResultList';
import { ApiActionStatus, type Offer, type PaleState, type ThunkDispatchFunc } from '@/interfaces';
import { DEFAULT_BOUNDS, offersToMapPois, parseBoundsFromQuery } from '@/lib/utils/mapData';
import { filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';
import { trackSearchThisArea } from '@/lib/utils/tracking';
import { actions as filterActions } from '@/state/filterActions';
import { fetchMapResults } from '@/state/mapResultActions';
import { fetchResults } from '@/state/resultActions';
import { MapOfferCard } from './MapOfferCard';

export const MapResultPage = () => {
    const router = useRouter();
    const dispatch = useDispatch<ThunkDispatchFunc>();

    const offers = useSelector((s: PaleState) => s.mapResults.offers);
    const resultStatus = useSelector((s: PaleState) => s.mapResults.status);
    const filters = useSelector((s: PaleState) => s.filters);

    const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [currentBounds, setCurrentBounds] = useState<MapBounds | null>(null);
    const originalBoundsRef = useRef<MapBounds | null>(null);
    const initialBoundsRef = useRef<MapBounds | null>(null);
    const hasInitializedRef = useRef(false);
    const hasRetriedInitialLoadRef = useRef(false);

    useEffect(() => {
        if (hasRetriedInitialLoadRef.current) return;
        const hasGeoSearchContext = Boolean(filters.boundingBox?.length);
        const shouldRetryOnEmptyHydration = resultStatus === ApiActionStatus.OK && offers.length === 0 && hasGeoSearchContext;

        if (resultStatus === ApiActionStatus.NOT_STARTED || resultStatus === ApiActionStatus.ERROR || shouldRetryOnEmptyHydration) {
            hasRetriedInitialLoadRef.current = true;
            dispatch(fetchResults());
            dispatch(fetchMapResults());
        }
    }, [dispatch, filters.boundingBox, offers.length, resultStatus]);

    const offerPois = useMemo(() => offersToMapPois(offers), [offers]);

    const primaryPois = useMemo(() => offerPois.filter((poi) => poi.id !== selectedOffer?.offerId), [offerPois, selectedOffer?.offerId]);

    const selectedPois = useMemo(() => offerPois.filter((poi) => poi.id === selectedOffer?.offerId), [offerPois, selectedOffer?.offerId]);

    // Calculate initial bounds: from redux filters (SSR) > from URL > from offers > default Europe
    // Only lock in the bounds once we have valid data
    // Also stores original bounds for use when closing the map
    const initialBounds = useMemo((): MapBounds => {
        // If we already locked in bounds, keep them
        if (hasInitializedRef.current && initialBoundsRef.current) {
            return initialBoundsRef.current;
        }

        if (filters.boundingBox?.length) {
            const minLat = Math.min(...filters.boundingBox.map((b) => b.minLat));
            const minLon = Math.min(...filters.boundingBox.map((b) => b.minLon));
            const maxLat = Math.max(...filters.boundingBox.map((b) => b.maxLat));
            const maxLon = Math.max(...filters.boundingBox.map((b) => b.maxLon));
            const bounds: MapBounds = {
                sw: [minLon, minLat],
                ne: [maxLon, maxLat],
            };
            initialBoundsRef.current = bounds;
            originalBoundsRef.current = bounds; // Store for close map
            hasInitializedRef.current = true;
            return bounds;
        }

        // Then check URL for saved bounds (for client-side navigation)
        if (router.isReady) {
            const urlBounds = parseBoundsFromQuery(router.query);
            if (urlBounds) {
                initialBoundsRef.current = urlBounds;
                originalBoundsRef.current = urlBounds; // Store for close map
                hasInitializedRef.current = true;
                return urlBounds;
            }
        }

        // Then try to fit all offers (only lock in if we have offers)
        if (offerPois.length > 0) {
            const lngs = offerPois.map((p) => p.coordinates[0]);
            const lats = offerPois.map((p) => p.coordinates[1]);
            const bounds = {
                sw: [Math.min(...lngs) - 0.5, Math.min(...lats) - 0.5] as [number, number],
                ne: [Math.max(...lngs) + 0.5, Math.max(...lats) + 0.5] as [number, number],
            };
            initialBoundsRef.current = bounds;
            originalBoundsRef.current = bounds; // Store for close map
            hasInitializedRef.current = true;
            return bounds;
        }

        return DEFAULT_BOUNDS;
    }, [filters.boundingBox, router.isReady, router.query, offerPois]);

    const handleUpdateBounds = useCallback((bounds: MapBounds) => {
        setCurrentBounds(bounds);
    }, []);

    const handleSearchThisArea = useCallback(() => {
        if (!currentBounds) return;

        trackSearchThisArea();
        dispatch(filterActions.clearGeoFilters());
        dispatch(
            filterActions.setBoundingBox([
                {
                    minLat: currentBounds.sw[1],
                    minLon: currentBounds.sw[0],
                    maxLat: currentBounds.ne[1],
                    maxLon: currentBounds.ne[0],
                },
            ]),
        );

        dispatch(fetchResults());
        dispatch(fetchMapResults());

        originalBoundsRef.current = currentBounds;
    }, [currentBounds, dispatch]);

    const handleFilterChange = useCallback(() => {
        dispatch(fetchResults());
        dispatch(fetchMapResults());
    }, [dispatch]);

    const handlePinClick = useCallback((id: string) => {
        setLoading(true);
        fetchOffer(getApiUrl(), { offerId: id })
            .then((res) => setSelectedOffer(res))
            .finally(() => setLoading(false));
    }, []);

    const handleCloseCard = useCallback(() => {
        setSelectedOffer(null);
    }, []);

    const toggleFilters = useCallback(() => {
        setFiltersOpen((prev) => !prev);
    }, []);

    const closeMapUrl = useMemo(() => {
        const queryString = stringifyParams(filtersToBrowserQueryParams(filters));
        return queryString ? `/reise/pakkereiser/resultat?${queryString}` : '/reise/pakkereiser/resultat';
    }, [filters]);

    return (
        <>
            <SimpleHead
                title="Pakkereiser - Kart | FINN reise"
                canonical="https://www.finn.no/reise/pakkereiser/kart"
                description="Se pakkereiser på kartet. Finn billige pakkereiser fra alle reisebyrå på ett sted!"
            />
            <main>
                <ResultPageMapLayout
                    showFilters={filtersOpen}
                    toggleFilters={toggleFilters}
                    resultList={<ResultList className="px-16" includeBanners={false} pageType="Map" />}
                    filters={<Filters onFilterChange={handleFilterChange} />}
                    numberOfOffers={offerPois.length}
                    closeMapUrl={closeMapUrl}
                    id="result-page-map"
                    primaryPois={primaryPois}
                    selectedPois={selectedPois}
                    initialBounds={initialBounds}
                    onPinClick={handlePinClick}
                    onUpdateBounds={handleUpdateBounds}
                    onUpdateBoundsClick={handleSearchThisArea}
                    hasClustering>
                    {(loading || selectedOffer) && (
                        <div className="maplibregl-ctrl-bottom-center">
                            <MapOfferCard offer={selectedOffer} onClose={handleCloseCard} />
                        </div>
                    )}
                </ResultPageMapLayout>
            </main>
        </>
    );
};
